import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { Text } from "components/designV2/text/text";
import { usePDFViewer } from "hooks/usePDFViewer";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColorV2 } from "config/colors";
import { LocalDate } from "@js-joda/core";
import { ViewerContainer, XmlContent } from "../salary/salary.styled";
import {
  ViewMode,
  ViewModeSwitcher,
} from "../salary/viewModeSwitcher.component";
import { ButtonsWrapper, PdfFrame } from "./fileViewer.styled";

type FileViewerProps = {
  fetchEndpoint: () => Promise<any>;
  filename: string;
  type: string;
  currentDate?: LocalDate;
  ListComponent?: React.ComponentType<{
    currentDate: LocalDate;
    data: any[];
    setData: React.Dispatch<React.SetStateAction<any[]>>;
  }>;
};

export const FileViewer: React.FC<FileViewerProps> = (props) => {
  const {
    viewFile,
    downloadFile,
    cleanupFile,
    fileUrl,
    fileContent,
    isLoading,
    prepareExport,
  } = usePDFViewer();
  const { t } = useTranslation();
  const [xmlContent, setXmlContent] = useState<string>("");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    setData([]);
  }, [props.currentDate]);

  const isPdf = props.type.includes("pdf");

  useEffect(() => {
    const loadFile = async () => {
      await viewFile(props.fetchEndpoint(), props.type);
    };

    loadFile();

    return () => {
      cleanupFile();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.fetchEndpoint, props.type]);

  useEffect(() => {
    if (fileContent && !isPdf) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (typeof e.target?.result === "string") {
          setXmlContent(e.target.result);
        }
      };
      reader.readAsText(fileContent);
    }
  }, [fileContent, isPdf]);

  if (isLoading) {
    return (
      <Text typography="b2" color={ColorV2.Blue1} align="center">
        {t("General.Loading")}
      </Text>
    );
  }

  if (!fileUrl) {
    return (
      <Text typography="b2" color={ColorV2.Blue1} align="center">
        {t("Salary.NoFileToShow")}
      </Text>
    );
  }

  const showXmlOrPdf = isPdf || viewMode === "xml";

  return (
    <>
      <ButtonsWrapper>
        {!isPdf && (
          <ViewModeSwitcher onChange={setViewMode} defaultMode={viewMode} />
        )}
        <PrimaryButton
          label={isPdf ? t("Salary.DownloadPdf") : t("Salary.DownloadXML")}
          size="small"
          onClick={() => downloadFile(props.filename)}
        />
        {!isPdf && (
          <PrimaryButton
            label={t("Salary.DownloadSIE")}
            size="small"
            onClick={() => {
              if (props.currentDate) {
                prepareExport(
                  props.currentDate.monthValue(),
                  "SIE",
                  props.currentDate.year(),
                );
              }
            }}
            style={{ marginLeft: 15 }}
          />
        )}
      </ButtonsWrapper>

      {showXmlOrPdf ? (
        <ViewerContainer>
          {isPdf ? (
            <PdfFrame src={`${fileUrl}#toolbar=1`} title="PDF Viewer" />
          ) : (
            <XmlContent>
              <pre>{xmlContent}</pre>
            </XmlContent>
          )}
        </ViewerContainer>
      ) : props.currentDate && props.ListComponent ? (
        <props.ListComponent
          currentDate={props.currentDate}
          data={data}
          setData={setData}
        />
      ) : null}
    </>
  );
};
